import React from 'react';

import { ParsedData } from '@betterment/js-runtime';
import { Text } from '@betterment/samba-react';
import { Box, Button, ErrorText, Flex, FormikDropdown } from '@betterment/style-closet-react';
import { startCase } from 'es-toolkit/compat';
import { ErrorMessage } from 'formik';
import * as Yup from 'yup';

import { Address, AddressParams, AddressValidationParams } from './data';
import { InputGroupWithLabelAndError, buildErrorMessagesFromApiErrors } from './form-helpers';

import { trackClick } from '../features/track';
import { InputGroup } from '../Shared/components-v2/input-group';
import { US_STATES } from '../Shared/constants';

export type AddressType = 'residentialAddress' | 'mailingAddress';

interface EditAddressFieldsProps {
  fieldNamePrefix: AddressType;
}

const EditAddressFields: React.FC<React.PropsWithChildren<EditAddressFieldsProps>> = ({ fieldNamePrefix }) => {
  return (
    <>
      <InputGroupWithLabelAndError
        fieldName={`${fieldNamePrefix}.addressLine1`}
        labelText="Address line 1"
        ariaLabel={`${startCase(fieldNamePrefix).toLowerCase()} address line 1`}
        autoFocus={true}
      />
      <InputGroupWithLabelAndError
        fieldName={`${fieldNamePrefix}.addressLine2`}
        labelText="Address line 2"
        ariaLabel={`${startCase(fieldNamePrefix).toLowerCase()} address line 2`}
      />
      <InputGroupWithLabelAndError
        fieldName={`${fieldNamePrefix}.city`}
        labelText="City"
        ariaLabel={`${startCase(fieldNamePrefix).toLowerCase()} city`}
      />

      <InputGroup>
        <FormikDropdown
          items={Object.keys(US_STATES).map(value => ({ value }))}
          name={`${fieldNamePrefix}.state`}
          labelText="State"
          aria-label={`${startCase(fieldNamePrefix).toLowerCase()} state`}
        />
        <ErrorMessage name={`${fieldNamePrefix}.state`} component={ErrorText} />
      </InputGroup>

      <InputGroupWithLabelAndError
        fieldName={`${fieldNamePrefix}.zip`}
        labelText="Zip code"
        ariaLabel={`${startCase(fieldNamePrefix).toLowerCase()} zip code`}
      />
    </>
  );
};

export const AddressSchema = {
  addressLine1: Yup.string().required('Enter a valid address'),
  addressLine2: Yup.string().nullable(),
  city: Yup.string().required('Enter a valid city'),
  state: Yup.string().oneOf(Object.keys(US_STATES)).required('Enter a valid state'),
  zip: Yup.string()
    .required('Enter a valid zip code')
    .matches(/^\d{5}$/, 'Enter a valid zip code'),
};

interface AddressFormProps {
  titleText: string;
  fieldNamePrefix: AddressType;
  onEdit: () => void;
  children: React.ReactElement;
}

interface CompoundAddressForm extends React.FC<AddressFormProps> {
  FormattedAddress: React.FC<React.PropsWithChildren<Address>>;
  Line: React.FC<React.PropsWithChildren<unknown>>;
}

export const AddressForm: CompoundAddressForm = ({ titleText, fieldNamePrefix, onEdit, children }) => {
  const [isEditing, setIsEditing] = React.useState(false);

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="flex-start" mb="1">
        <Text
          as="p"
          css={{
            fontWeight: 'bold',
            lineHeight: 'lineHeight.30',
            marginBottom: 'space.0',
          }}
        >
          {titleText}
        </Text>
        {!isEditing && (
          <Button
            type="button"
            variant="tertiary"
            aria-label={`edit ${titleText.toLowerCase()}`}
            onClick={() => {
              trackClick({ name: 'EditAddress', flow: 'OpenCheckingAccount' });
              setIsEditing(true);
              onEdit();
            }}
          >
            Edit
          </Button>
        )}
      </Flex>
      {isEditing ? <EditAddressFields fieldNamePrefix={fieldNamePrefix} /> : <Box pb="1">{children}</Box>}
    </Box>
  );
};

AddressForm.Line = ({ children }) => {
  return (
    <Text
      as="p"
      css={{
        lineHeight: 'lineHeight.100',
        marginBottom: 'space.0',
      }}
    >
      {children}
    </Text>
  );
};

AddressForm.FormattedAddress = ({ addressLine1, addressLine2, city, state, zip }) => {
  return (
    <>
      <AddressForm.Line>
        {addressLine1}
        {addressLine2 && `, ${addressLine2}`}
      </AddressForm.Line>
      <AddressForm.Line>
        {city}, {state} {zip}
      </AddressForm.Line>
    </>
  );
};

type AddressValidator = (data?: AddressValidationParams) => Promise<ParsedData<{}>>;

export function buildRemoteAddressValidation(addressValidator: AddressValidator) {
  // eslint-disable-next-line func-names
  return async function (this: Yup.TestContext, values: Partial<AddressParams>) {
    const [residentialResponse, mailingResponse] = await Promise.all([
      addressValidator({ isResidential: true, ...values }),
      addressValidator({ isResidential: false, ...values }),
    ]);
    let errors: Yup.ValidationError[] = [];
    if (residentialResponse?.errors) {
      errors = errors.concat(
        buildErrorMessagesFromApiErrors.call(this, residentialResponse.errors, 'residentialAddress')
      );
    }
    if (mailingResponse?.errors) {
      errors = errors.concat(buildErrorMessagesFromApiErrors.call(this, mailingResponse.errors, 'mailingAddress'));
    }
    if (errors.length > 0) {
      const validationError = new Yup.ValidationError(errors, {}, '');
      return validationError;
    }
    return true;
  };
}

export function buildResidentialAddressValidation(addressValidator: AddressValidator) {
  // eslint-disable-next-line func-names
  return async function (this: Yup.TestContext, values: Partial<AddressParams>) {
    const [residentialResponse] = await Promise.all([addressValidator({ isResidential: true, ...values })]);

    let errors: Yup.ValidationError[] = [];
    if (residentialResponse?.errors) {
      errors = errors.concat(
        buildErrorMessagesFromApiErrors.call(this, residentialResponse.errors, 'residentialAddress')
      );
    }
    if (errors.length > 0) {
      const validationError = new Yup.ValidationError(errors, {}, '');
      return validationError;
    }
    return true;
  };
}
